/**
 * Whether the view still matches the report it was loaded from.
 *
 * The comparison itself lives in utils/reportFingerprint.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { useGridApiRef } from '@mui/x-data-grid-premium';

import type { SummaryReport, SummaryReportStatus } from '../types.ts';
import {
    summaryReportFingerprint,
    type SummaryReportSnapshot,
} from '../utils/reportFingerprint.ts';

/**
 * How long the grid has to sit still before its state is compared.
 *
 * Long enough to cover a restore's several passes, short enough to keep up with an edit the
 * user just made.
 */
export const STATE_SETTLE_MS = 250;

export function useSummaryReportStatus({
    apiRef,
    activeReport,
    definitionExpression,
    exportGridState,
}: {
    apiRef: ReturnType<typeof useGridApiRef>;
    activeReport: SummaryReport | null;
    definitionExpression: string;
    /** The View's snapshot of the grid — the same pieces a save is built from. */
    exportGridState: () => Omit<SummaryReportSnapshot, 'definitionExpression'>;
}): SummaryReportStatus {
    // A counter, not the state itself: what changed doesn't matter, only that the comparison
    // is worth making again.
    const [settled, setSettled] = useState(0);

    useEffect(() => {
        if (!apiRef.current) return undefined;

        let timer: ReturnType<typeof setTimeout> | undefined;
        const scheduleRecheck = () => {
            clearTimeout(timer);
            timer = setTimeout(() => setSettled((count) => count + 1), STATE_SETTLE_MS);
        };

        const unsubscribe = apiRef.current.subscribeEvent('stateChange', scheduleRecheck);
        return () => {
            clearTimeout(timer);
            unsubscribe();
        };
    }, [apiRef]);

    // Where the count stood when this report became the active one.
    //
    // A load reaches the View well before it reaches the grid, and comparing in that window
    // finds a grid still catching up and calls it unsaved. So a report stays clean until a
    // settle has been seen since it arrived. Written during render because the answer is
    // needed in the same pass the report changes in.
    const arrivedFor = useRef<SummaryReport | null>(null);
    const arrivedAt = useRef(0);
    if (arrivedFor.current !== activeReport) {
        arrivedFor.current = activeReport;
        arrivedAt.current = settled;
    }

    return useMemo(() => {
        if (!activeReport) return 'none';
        if (settled === arrivedAt.current) return 'clean';

        const current = summaryReportFingerprint({ ...exportGridState(), definitionExpression });
        const saved = summaryReportFingerprint({
            gridState: activeReport.gridState,
            pivotState: activeReport.pivotState,
            chartState: activeReport.chartState,
            definitionExpression: activeReport.definitionExpression,
        });

        return current === saved ? 'clean' : 'dirty';
        // `settled` is the reason to recompute rather than an input to the result: the grid
        // state it stands for is read through the api, where React can't see it.
    }, [settled, activeReport, definitionExpression, exportGridState]);
}
